import { useEffect, useState } from 'react'
import Menu from '@mui/material/Menu'
import styled from 'styled-components'
import MenuItem from '@mui/material/MenuItem'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import {
  DeleteRed,
  ChatMenu,
  BulkActions,
  MarkAsRead,
  BulkFavourites,
  BulkFavouritesRemoved
} from '@/components/icons'
import { colors } from '@/styles'
import { showDeleteModal } from '../chat-room/delete-modal'

const ButtonWrapper = styled.div`
  cursor: pointer;
  position: absolute;
  top: 24px;
`

const ItemContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 15px;
  background-color: ${colors.white};

  :hover {
    opacity: 0.7;
  }
`

const DeleteContainer = styled(ItemContainer)`
  padding: 15px 15px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`

const IconWrapper = styled.div`
  margin-right: 10px;
`

const ItemText = styled.span`
  font-size: 14px;
  color: ${colors.grey24};
`

const DeleteText = styled(ItemText)`
  color: ${colors.red64};
`

const ChatMenuWrapper = styled(ChatMenu)`
  width: 44px;
  height: 44px;
`

const ActionEllipsis = styled.div`
  &:after {
    position: relative;
    top: -10px;
    content: '\\2026';
    color: ${colors.white};
    font-size: 40px;
  }
`

const MenuDropdown = ({
  timelineRoom,
  is_favourite,
  target,
  setSelectMode,
  setIsMultiSelectOpen,
  dragged,
  setDragged,
  name
}) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null)
  const isMobile = target === 'mobile'
  const isFavourite = is_favourite === '1'
  const open = Boolean(anchorEl)
  const menuId = open ? 'simple-popover' : undefined

  useEffect(() => {
    // Stuck to a useEffect because adding this directly to handleClose causes a glitch in the setDragged animation.
    if (!open && dragged === 'dragLeft') {
      setDragged('dragRight')
    }
  }, [open])

  const handleClick = e => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }
  const handleClose = () => {
    setIsMultiSelectOpen(false)
    setAnchorEl(null)
  }

  const handleBulkActions = e => {
    e.stopPropagation()
    setSelectMode(true)
    handleClose()
  }

  const handleAddFaviourites = e => {
    e.stopPropagation()
    isFavourite
      ? dispatch.chat.removeTimelineToFavourites(timelineRoom)
      : dispatch.chat.addTimelineToFavourites(timelineRoom)
    handleClose()
  }

  const handleMarkAsRead = e => {
    e.stopPropagation()
    dispatch.chat.setTimelineRead(timelineRoom)
    handleClose()
  }

  const handleDelete = e => {
    e.stopPropagation()
    showDeleteModal({ name, timelineRoomId: timelineRoom, history })
    handleClose()
  }

  const { t } = useTranslation()
  return (
    <>
      <ButtonWrapper aria-describedby={menuId} aria-haspopup="true" onClick={handleClick}>
        {isMobile ? <ActionEllipsis /> : <ChatMenuWrapper />}
      </ButtonWrapper>
      <Menu
        id={menuId}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            border: `1.5px solid ${colors.grey88}`,
            boxShadow:
              '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
            width: 263,
            borderRadius: '6px'
          },
          '& .MuiMenuItem-root': {
            padding: 0
          },
          '& .MuiList-root': {
            padding: 0
          }
        }}
      >
        <MenuItem>
          <ItemContainer onClick={handleBulkActions}>
            <IconWrapper>
              <BulkActions fill={'#8F8F8F'} />
            </IconWrapper>
            <ItemText>{t('b.chat.view.bulk.actions', 'Bulk actions')}</ItemText>
          </ItemContainer>
        </MenuItem>
        <MenuItem>
          <ItemContainer onClick={handleMarkAsRead}>
            <IconWrapper>
              <MarkAsRead fill={'#8F8F8F'} />
            </IconWrapper>
            <ItemText>{t('b.chat.view.bulk_edit.menu.mark_as_read', 'Mark as read')}</ItemText>
          </ItemContainer>
        </MenuItem>
        <MenuItem>
          <ItemContainer onClick={handleAddFaviourites}>
            <IconWrapper>
              {isFavourite ? <BulkFavourites fill={'#8F8F8F'} /> : <BulkFavouritesRemoved />}
            </IconWrapper>
            <ItemText>
              {isFavourite
                ? t('b.chat.view.remove_from_favourites.button', 'Remove from faviourites')
                : t('b.chat.view.favourites.button_add', 'Add to favourites')}
            </ItemText>
          </ItemContainer>
        </MenuItem>
        <MenuItem>
          <DeleteContainer onClick={handleDelete}>
            <IconWrapper>
              <DeleteRed />
            </IconWrapper>
            <DeleteText>{t('b.chat.view.menu_delete_chat.button', 'Delete chat')}</DeleteText>
          </DeleteContainer>
        </MenuItem>
      </Menu>
    </>
  )
}

export default MenuDropdown
